import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { units } from "@/lib/format"
import type { NumberedAction } from "../lib/actions"
import { type ChannelSeries, type SalesPoint, channelSeries, salesMarks, salesSeries, seriesUnits } from "../lib/salesSeries"
import type { SalesHistory } from "../types"
import { ActionKey, MARKED_TOP, actionLines } from "./ActionMarks"

const AXIS_TICK = { fontSize: 10, fill: "var(--chart-axis)" }

function Swatch({ color }: { color: string }) {
  return <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: color }} />
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <Swatch color={color} />
      {label}
    </span>
  )
}

// Every stack with units out in the point, then the total.
function SalesTooltip({ point, series }: { point: SalesPoint; series: ChannelSeries[] }) {
  const rows = series.map((s) => ({ s, out: seriesUnits(point, s) })).filter(({ out }) => out.sold + out.gifted > 0)

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs text-foreground shadow-sm">
      <div className="mb-1 font-semibold">{point.title}</div>
      <table className="tabular-nums">
        <thead>
          <tr>
            <th />
            <th className="pl-4 text-right font-medium">Sold</th>
            <th className="pl-4 text-right font-medium">Gifted</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ s, out }) => (
            <tr key={s.key}>
              <td className="py-0.5">
                <span className="flex items-center gap-1.5"><Swatch color={s.color} />{s.name}</span>
              </td>
              <td className="pl-4 text-right">{units(out.sold)}</td>
              <td className="pl-4 text-right">{units(out.gifted)}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="pt-1">Total</td>
            <td className="pl-4 pt-1 text-right">{units(point.sold)}</td>
            <td className="pl-4 pt-1 text-right">{units(point.gifted)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// Movement By Channel: units out (sold plus gifted) in this market over the
// last 6 calendar months (this one so far), one bar per channel side by side
// (org-tree order, the same color from product to product), with the Push
// review actions marked on the months they ran. The tooltip splits sold and
// gifted. Only the channels with units out in the graph get a bar and a
// legend entry.
export default function StockMovementChart({ history, today, actions }: { history: SalesHistory; today: string; actions: NumberedAction[] }) {
  const points = salesSeries(history.days, "month", today)
  const marks = salesMarks(history.days, "month", today, actions)
  const series = channelSeries(history.channels)
  const shown = series.filter((s) => points.some((p) => { const out = seriesUnits(p, s); return out.sold + out.gifted > 0 }))

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-foreground">Units sold or gifted per channel, this market</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={points} margin={{ top: marks.length > 0 ? MARKED_TOP : 16, right: 24, bottom: 0, left: 0 }} barCategoryGap="15%" barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
          <XAxis
            dataKey="i"
            tickFormatter={(i: number) => points[i]?.tick ?? ""}
            interval={0}
            tick={AXIS_TICK}
          />
          <YAxis width={48} tick={AXIS_TICK} allowDecimals={false} tickFormatter={(value: number) => units(value)} />
          <Tooltip
            content={({ active, label }) => {
              const point = points[Number(label)]
              return active && point ? <SalesTooltip point={point} series={series} /> : null
            }}
            cursor={{ fill: "var(--muted)" }}
          />
          {shown.map((s) => (
            <Bar
              key={s.key}
              dataKey={(p: SalesPoint) => { const out = seriesUnits(p, s); return out.sold + out.gifted }}
              name={s.name}
              fill={s.color}
              maxBarSize={16}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          ))}
          {actionLines(marks)}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-foreground">
        {shown.map((s) => <LegendItem key={s.key} color={s.color} label={s.name} />)}
      </div>
      <ActionKey marks={marks} today={today} />
    </div>
  )
}
